import React, { useEffect } from "react";
import "./static.css";
import { Helmet } from "react-helmet";

const DummyPage: React.FC = () => {
  useEffect(() => {
    document.title = "Methodology";
  }, []);

  return (
    <div className="dummy-container-static">
      <Helmet>
        <title>Methodology</title>
        <meta
          name="description"
          content="Bloat@Brown features an AI algorithm that analyzes every Brown
        administrator in three domains: legality, redundancy, and bullshit jobs."
        />
      </Helmet>
      <h1 className="dummy-heading1-static">Methodology</h1>
      <p className="dummy-paragraph-static">
        Bloat@Brown features an AI algorithm that analyzes every Brown
        administrator in three domains: legality, redundancy, and bullshit jobs.
        The algorithm flags administrators whose roles could be problematic. A
        flag is not a conclusive finding but a signal that further investigation
        is warranted.
      </p>
      <h2 className="dummy-heading2-static">Retrieval-Augmented Generation</h2>
      <p className="dummy-paragraph-static">
        Bloat@Brown's algorithm scrapes publicly available internet sources for
        each employee and role (brown.edu pages, articles, LinkedIn pages, job
        postings, etc.) and searches our database for co-workers with similar
        roles—similar to how a human would conduct internet research. These
        online sources are passed to an LLM, which makes its judgments solely
        based on the retrieved sources.
      </p>
      <h2 className="dummy-heading2-static">Scoring Metrics</h2>
      <p className="dummy-paragraph-static">
        The LLM assigns ratings to each employee by considering all publicly
        available information and answering the following questions.
      </p>
      <h2 className="dummy-subheading-static">Legality Subscores</h2>
      <p className="dummy-paragraph-static">
        <b>DEI</b>: Does the role involve diversity, equity, and inclusion,
        which may violate the Civil Rights Act following the Supreme Court's
        ruling in Students for Fair Admissions v. Harvard?
      </p>
      <p className="dummy-paragraph-static">
        <b>Antisemitism</b>: Has the employee publicly voiced antisemitic ideas,
        which the Trump Administration asserts is a violation of the Civil
        Rights Act?
      </p>
      <h2 className="dummy-subheading-static">Redundancy Subscores</h2>
      <p className="dummy-paragraph-static">
        <b>Automation</b>: Can the role be automated using current technology?
      </p>
      <p className="dummy-paragraph-static">
        <b>Overlap</b>: Is the employee assigned to responsibilities that could
        otherwise be completed by other employees with overlapping
        responsibilities?
      </p>
      <h2 className="dummy-subheading-static">Bullshit Job Subscores</h2>
      <p className="dummy-paragraph-static">
        "Bullshit Jobs: A Theory" is a 2018 book by David Graeber that describes
        five types of useless jobs.
      </p>
      <p className="dummy-paragraph-static">
        <b>Flunkie</b>: Does the role exist primarily to make one's boss feel
        more important (e.g., receptionists, assistants, etc.)?
      </p>
      <p className="dummy-paragraph-static">
        <b>Goon</b>: Is the employee's primary responsibility to fight or
        deceive others on behalf of the university (e.g., litigators, PR
        specialists, marketers, etc.)?
      </p>
      <p className="dummy-paragraph-static">
        <b>Duct Taper</b>: Is the employee's primary responsibility to make
        temporary fixes to systems that could be permanently streamlined (e.g.,
        data entry, IT maintenance staff, etc.)?
      </p>
      <p className="dummy-paragraph-static">
        <b>Box Ticker</b>: Is the employee's primary responsibility to create
        superfluous outputs (e.g., memo writers, newsletter writers, etc.)?
      </p>
      <p className="dummy-paragraph-static">
        <b>Taskmaster</b>: Is the employee a manager whose primary
        responsibility is to assign unnecessary tasks to their subordinates
        (e.g., middle management)?
      </p>
      <h2 className="dummy-heading2-static">Ratings</h2>
      <p className="dummy-paragraph-static">
        On each metric, each employee is assigned a rating of low risk,
        ambiguous, or suspect. Low risk indicates that the employee is unlikely
        to meet the criteria, ambiguous means it is unclear based on public
        information, and suspect means public information indicates that the
        employee might meet the criteria.
      </p>
      <p className="dummy-paragraph-static">
        All employees who are rated ambiguous or suspect in any metric have been
        contacted by email so they can provide more details on their situation.
        These responses will be subjected to manual review.
      </p>
      <h2 className="dummy-heading2-static">See More</h2>
      <p className="dummy-paragraph-static">
        <a
          className="dummy-link-static"
          href="/contact"
          rel="noopener noreferrer"
        >
          Contact the team
        </a>
      </p>
      <p className="dummy-paragraph-static">
        <a className="dummy-link-static" href="/" rel="noopener noreferrer">
          Explore Brown's org chart
        </a>
      </p>
      <p className="dummy-paragraph-static">
        <a
          className="dummy-link-static"
          href="https://x.com/brown_spectator"
          target="_blank"
          rel="noopener noreferrer"
        >
          Check us out on X
        </a>
      </p>
    </div>
  );
};

export default DummyPage;
